import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, AUTH_LOGOUT_EVENT } from "@/api/ApiClient";
import { tokenStorage } from "@/auth/tokenStorage";
import type { AuthSessionResponse } from "@/auth/authTypes";
import { useTranslation } from "@/hooks/useTranslation";
import { initializeRevenueCat } from "@/lib/revenueCatInit";
import { logInRevenueCatUser, logOutRevenueCatUser } from "@/lib/revenueCatClient";

export interface AppUser {
  id: string;
  email?: string;
  name?: string;
  currentTokens?: number;
  isPremium?: boolean;
  gender?: "male" | "female" | "non-binary";
  ageRange?: string;
  looking_for?: string;
  fulfillment_level?: string;
  communication_style?: string;
  notifications_allowed?: boolean;
  preferredLanguage?: string;
  emailVerified?: boolean;
}

type AuthContextValue = {
  user: AppUser | null;
  setUser: React.Dispatch<React.SetStateAction<AppUser | null>>;
  initializing: boolean;
  applySession: (session: AuthSessionResponse) => Promise<void>;
  /** openLogin false: land on the landing page without the login form (e.g. after account deletion). */
  logout: (opts?: { openLogin?: boolean }) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** `location.state` after logout / forced session end — {@link Index} opens the login modal. */
export const LOGOUT_REDIRECT_STATE = { openLogin: true as const };

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { syncLanguageFromServer } = useTranslation();
  const [user, setUser] = useState<AppUser | null>(null);
  const [initializing, setInitializing] = useState(true);
  // Whether the stored session has been checked once; a session that turns
  // out dead during that check is dropped quietly (the app opens on the
  // landing page like for a signed-out user), only a session that dies
  // later, mid-use, sends the user to the login form.
  const bootstrappedRef = useRef(false);

  const refreshUserProfile = useCallback(async () => {
    try {
      const userData = (await apiClient.getUserProfile()) as Record<string, unknown>;
      if (userData.preferredLanguage && typeof userData.preferredLanguage === "string") {
        syncLanguageFromServer(userData.preferredLanguage);
      }
      const mapped: Partial<AppUser> = {
        ...userData,
        emailVerified: Boolean(userData.emailVerified),
        notifications_allowed: Boolean(userData.notificationsAllowed),
        isPremium: Boolean(userData.premium),
        currentTokens:
          typeof userData.currentTokens === "number" ? userData.currentTokens : undefined,
      };
      const idStr = String(userData.id);
      setUser((prev) => ({ ...(prev ?? { id: idStr }), ...mapped, id: idStr }));
      await tokenStorage.setUserProfileOnly({
        id: idStr,
        email: (userData.email as string) ?? null,
        name: (userData.name as string) ?? null,
      });
    } catch {
      /* offline or expired without refresh */
    }
  }, [syncLanguageFromServer]);

  const applySession = useCallback(async (session: AuthSessionResponse) => {
    await apiClient.applyAuthSession(session);
    setUser({
      id: session.user.id,
      email: session.user.email ?? undefined,
      name: session.user.name ?? undefined,
    });
  }, []);

  const logout = useCallback(async (opts?: { openLogin?: boolean }) => {
    await apiClient.clearAllTokens();
    // Leaving on purpose (or deleting the account): the device login goes too.
    await tokenStorage.clearDeviceLogin();
    setUser(null);
    try {
      localStorage.removeItem("localUser");
    } catch {
      /* ignore */
    }
    navigate("/", { replace: true, state: opts?.openLogin === false ? {} : LOGOUT_REDIRECT_STATE });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await apiClient.hydrateFromStorage();
      let profile = await tokenStorage.getUserProfile();
      // No stored session (e.g. it died while offline): an account without
      // e-mail can still come back through its device login.
      if ((!apiClient.token || !profile) && (await apiClient.signInWithDeviceLogin())) {
        profile = await tokenStorage.getUserProfile();
      }
      if (!cancelled && apiClient.token && profile) {
        setUser({
          id: profile.id,
          email: profile.email ?? undefined,
          name: profile.name ?? undefined,
        });
        try {
          await refreshUserProfile();
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) {
        bootstrappedRef.current = true;
        setInitializing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once
  }, []);

  useEffect(() => {
    const onForceLogout = () => {
      // Read the flag now: the bootstrap may finish while the tokens are
      // being cleared, and a startup logout must stay silent.
      const openLogin = bootstrappedRef.current;
      void (async () => {
        await apiClient.clearAllTokens();
        setUser(null);
        navigate("/", { replace: true, state: openLogin ? LOGOUT_REDIRECT_STATE : {} });
      })();
    };
    window.addEventListener(AUTH_LOGOUT_EVENT, onForceLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, onForceLogout);
  }, [navigate]);

  // Initialize RevenueCat on native shells and keep RC user in sync with app user.
  // No-op on web.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await initializeRevenueCat();
        if (cancelled) return;
        if (user?.id) {
          await logInRevenueCatUser(user.id);
        } else {
          await logOutRevenueCatUser();
        }
      } catch (err) {
        console.warn("[RevenueCat] session sync failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const value = useMemo(
    () => ({
      user,
      setUser,
      initializing,
      applySession,
      logout,
      refreshUserProfile,
    }),
    [user, initializing, applySession, logout, refreshUserProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
